/**
 * Activity Tracker Utility
 * 
 * This utility tracks user activity and automatically logs out users after
 * a specified period of inactivity. It tracks mouse movement, keyboard input,
 * and other user interactions.
 */

import { getToken, removeToken } from './authUtils';

class ActivityTracker {
  constructor() {
    this.inactivityTimeout = 15 * 60 * 1000; // 15 minutes in milliseconds
    this.warningTimeout = 14 * 60 * 1000; // 14 minutes in milliseconds (1 minute before logout)
    this.activityTimer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();
    this.isTracking = false;
    this.onInactivityWarning = null;
    this.onInactivityLogout = null;
    this.isRecruiter = false;
    this.activityData = {
      totalActiveTime: 0,
      sessionStartTime: null,
      lastReportTime: null,
      mouseMovements: 0,
      keystrokes: 0,
      clicks: 0,
      loginTime: null,
      logoutTime: null,
      loginHistory: [],
      totalLoginHours: 0};
    
    // Bind methods to maintain context
    this.resetTimer = this.resetTimer.bind(this);
    this.checkInactivity = this.checkInactivity.bind(this);
    this.trackActivity = this.trackActivity.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.reportActivity = this.reportActivity.bind(this);
  }

  /**
   * Start tracking user activity
   * @param {boolean} isRecruiter - Whether the current user is a recruiter
   * @param {Function} onWarning - Callback when inactivity warning is triggered
   * @param {Function} onLogout - Callback when inactivity logout is triggered
   * @param {string} userId - The user ID for tracking login sessions
   */
  startTracking(isRecruiter = false, onWarning = null, onLogout = null, userId = null) {
    if (this.isTracking) return;
    
    this.isRecruiter = isRecruiter;
    this.onInactivityWarning = onWarning;
    this.onInactivityLogout = onLogout;
    this.userId = userId;
    this.isTracking = true;
    this.lastActivity = Date.now();
    
    // Record login time
    const loginTime = Date.now();
    this.activityData.sessionStartTime = loginTime;
    this.activityData.lastReportTime = loginTime;
    this.activityData.loginTime = loginTime;
    
    // Add to login history
    this.activityData.loginHistory.push({
      loginTime: loginTime,
      logoutTime: null,
      duration: 0});
    
    // Report login event
    if (isRecruiter && userId) {
      this.reportLoginEvent(loginTime);
    }
    
    // Only set up auto-logout for recruiters
    if (isRecruiter) {
      // Add event listeners for user activity
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('keypress', this.handleKeyPress);
      window.addEventListener('click', this.handleClick);
      window.addEventListener('scroll', this.resetTimer);
      window.addEventListener('touchmove', this.resetTimer);
      
      // Set initial timers
      this.resetTimer();
      
      // Set up periodic activity reporting
      this.activityReportInterval = setInterval(this.reportActivity, 60000); // Report every minute

    } else {
      // For non-recruiters, just track activity without auto-logout
      window.addEventListener('mousemove', this.trackActivity);
      window.addEventListener('keypress', this.trackActivity);
      window.addEventListener('click', this.trackActivity);
      
      // Set up periodic activity reporting
      this.activityReportInterval = setInterval(this.reportActivity, 300000); // Report every 5 minutes

    }
  }

  /**
   * Stop tracking user activity
   * @param {boolean} isLogout - Whether this is called during logout
   */
  stopTracking(isLogout = false) {
    if (!this.isTracking) return;
    
    // Clear timers
    clearTimeout(this.activityTimer);
    clearTimeout(this.warningTimer);
    clearInterval(this.activityReportInterval);
    
    // Remove event listeners
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('keypress', this.handleKeyPress);
    window.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.resetTimer);
    window.removeEventListener('touchmove', this.resetTimer);
    window.removeEventListener('mousemove', this.trackActivity);
    window.removeEventListener('keypress', this.trackActivity);
    window.removeEventListener('click', this.trackActivity);
    
    // Record logout time
    const logoutTime = Date.now();
    this.activityData.logoutTime = logoutTime;
    
    // Update the current login session
    if (this.activityData.loginHistory.length > 0) {
      const currentSession = this.activityData.loginHistory[this.activityData.loginHistory.length - 1];
      currentSession.logoutTime = logoutTime;
      currentSession.duration = (logoutTime - currentSession.loginTime) / 1000; // duration in seconds
      
      // Update total login hours
      this.activityData.totalLoginHours += currentSession.duration / 3600; // convert seconds to hours
    }
    
    // Final activity report before stopping
    this.reportActivity();
    
    // Report logout event
    if (this.isRecruiter && this.userId && isLogout) {
      this.reportLogoutEvent(logoutTime);
    }
    
    this.isTracking = false;

  }

  /**
   * Reset the inactivity timer
   */
  resetTimer() {
    this.lastActivity = Date.now();
    
    // Clear existing timers
    clearTimeout(this.activityTimer);
    clearTimeout(this.warningTimer);
    
    // Set warning timer (1 minute before logout)
    this.warningTimer = setTimeout(() => {
      if (this.onInactivityWarning) {
        this.onInactivityWarning();
      }
    }, this.warningTimeout);
    
    // Set logout timer
    this.activityTimer = setTimeout(this.checkInactivity, this.inactivityTimeout);
  }

  /**
   * Check if user has been inactive and log them out if necessary
   */
  checkInactivity() {
    const currentTime = Date.now();
    const inactiveTime = currentTime - this.lastActivity;
    
    if (inactiveTime >= this.inactivityTimeout) {

      // Stop tracking with logout flag
      this.stopTracking(true);
      
      // Log the user out
      if (this.onInactivityLogout) {
        this.onInactivityLogout();
      } else {
        // Default logout behavior
        removeToken();
        window.location.href = '/auth/employer';
      }
    }
  }

  /**
   * Track mouse movement with throttling to avoid excessive event handling
   */
  handleMouseMove(event) {
    // Throttle mouse movement tracking
    if (!this.lastMouseMoveTime || Date.now() - this.lastMouseMoveTime > 100) {
      this.lastMouseMoveTime = Date.now();
      this.activityData.mouseMovements++;
      this.resetTimer();
    }
  }

  /**
   * Track keypress events
   */
  handleKeyPress() {
    this.activityData.keystrokes++;
    this.resetTimer();
  }

  /**
   * Track click events
   */
  handleClick() {
    this.activityData.clicks++;
    this.resetTimer();
  }

  /**
   * Track activity for non-recruiters (without auto-logout)
   */
  trackActivity() {
    const currentTime = Date.now();
    this.lastActivity = currentTime;
    
    // Update activity metrics
    if (currentTime - this.lastTrackTime > 100) {
      this.lastTrackTime = currentTime;
      this.activityData.mouseMovements++;
    }
  }

  /**
   * Report user activity to the server
   */
  reportActivity() {
    if (!this.isTracking) return;
    
    const currentTime = Date.now();
    const sessionDuration = currentTime - this.activityData.sessionStartTime;
    const timeSinceLastReport = currentTime - this.activityData.lastReportTime;
    
    // Only report if we have a valid token
    if (!getToken()) {

      return;
    }
    
    // Prepare activity data
    const activityReport = {
      activeTime: Math.floor(timeSinceLastReport / 1000), // Convert to seconds
      totalSessionTime: Math.floor(sessionDuration / 1000), // Convert to seconds
      mouseMovements: this.activityData.mouseMovements,
      keystrokes: this.activityData.keystrokes,
      clicks: this.activityData.clicks,
      timestamp: new Date().toISOString(),
      userId: this.userId,
      loginTime: this.activityData.loginTime ? new Date(this.activityData.loginTime).toISOString() : null};
    
    // Reset counters for next report
    this.activityData.mouseMovements = 0;
    this.activityData.keystrokes = 0;
    this.activityData.clicks = 0;
    this.activityData.lastReportTime = currentTime;
    
    // Report activity to server (if user is a recruiter)
    if (this.isRecruiter && this.userId) {
      try {
        fetch('/api/activity/report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`},
          body: JSON.stringify(activityReport)}).catch(err => {
          console.error('Failed to report activity:', err);
        });
      } catch (error) {
        console.error('Error reporting activity:', error);
      }
    }
  }

  /**
   * Report login event to the server
   * @param {number} loginTime - The login timestamp
   */
  reportLoginEvent(loginTime) {
    if (!getToken() || !this.userId) return;
    
    try {
      fetch('/api/activity/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`},
        body: JSON.stringify({
          userId: this.userId,
          loginTime: new Date(loginTime).toISOString()})}).catch(err => {
        console.error('Failed to report login event:', err);
      });
    } catch (error) {
      console.error('Error reporting login event:', error);
    }
  }

  /**
   * Report logout event to the server
   * @param {number} logoutTime - The logout timestamp
   */
  reportLogoutEvent(logoutTime) {
    if (!getToken() || !this.userId) return;
    
    try {
      fetch('/api/activity/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`},
        body: JSON.stringify({
          userId: this.userId,
          logoutTime: new Date(logoutTime).toISOString(),
          loginTime: this.activityData.loginTime ? new Date(this.activityData.loginTime).toISOString() : null,
          sessionDuration: this.activityData.loginTime ? (logoutTime - this.activityData.loginTime) / 1000 : 0, // in seconds
        })}).catch(err => {
        console.error('Failed to report logout event:', err);
      });
    } catch (error) {
      console.error('Error reporting logout event:', error);
    }
  }

  /**
   * Get the current activity data
   */
  getActivityData() {
    return {
      ...this.activityData,
      lastActivity: this.lastActivity,
      currentSessionTime: Date.now() - this.activityData.sessionStartTime,
      loginHistory: this.activityData.loginHistory,
      totalLoginHours: this.activityData.totalLoginHours};
  }
  
  /**
   * Get login hours for the current session and total
   */
  getLoginHours() {
    const currentTime = Date.now();
    let currentSessionHours = 0;
    
    // Calculate current session hours if logged in
    if (this.activityData.loginTime) {
      currentSessionHours = (currentTime - this.activityData.loginTime) / (1000 * 60 * 60);
    }
    
    return {
      currentSession: currentSessionHours,
      total: this.activityData.totalLoginHours + currentSessionHours,
      loginHistory: this.activityData.loginHistory
    };
  }
}

// Create a singleton instance
const activityTracker = new ActivityTracker();

export default activityTracker;
